/**
 * Stage × Mechanism Validator — Canon 23 §7.2
 *
 * Client-side gate for session creation: given the 7 scenario dimensions,
 * returns the validity level and advisory notes for the combination.
 *
 * - `not_valid`  → hard block (session creation must be prevented)
 * - `warning`    → allowed; advisory must be surfaced in the UI
 * - `required`   → allowed; UI should present this as the recommended choice
 * - `valid`      → allowed without comment
 *
 * PARITY CONTRACT: this module must produce byte-identical results to
 * `scripts/generate_cycle_scenarios.py` (the test oracle that generates
 * `scripts/cycle_scenarios_all.csv`). The parity test in
 * `stageToolValidator.oracle.test.ts` enforces this. If the rules change,
 * change both files and regenerate the CSVs.
 *
 * AUTHORITY NOTE (Doc 01 §9.5): tool prerequisites are server-authoritative
 * via the `evaluate_tool_readiness()` RPC. This validator is the client-side
 * mirror used for UI gating and test generation — it complements the RPC,
 * it does not replace it.
 */

import type { MechanismTag } from './methodRegistry';
import type { CanonicalSessionType } from '../types/prioritization/session';

// ============================================================================
// Dimension types
// ============================================================================

export type ScenarioContext = 'cycle' | 'group' | 'personal';
export type ScenarioRunMode = 'facilitator_live' | 'participant_live' | 'open';
export type ScenarioExchangeState = 'not_applicable' | 'request_received' | 'revision_received';
export type ScenarioTournament = 'single_run' | 'tournament_round';
export type ScenarioPrerequisites = 'all_met' | 'partial' | 'none';

export type ScenarioValidity = 'required' | 'valid' | 'warning' | 'not_valid';

export interface ScenarioInput {
  context: ScenarioContext;
  stage: CanonicalSessionType;
  runMode: ScenarioRunMode;
  mechanism: MechanismTag;
  exchangeState: ScenarioExchangeState;
  tournament: ScenarioTournament;
  prerequisites: ScenarioPrerequisites;
}

export interface ScenarioVerdict {
  validity: ScenarioValidity;
  /** Semicolon-joined advisory/block reasons, in rule-evaluation order. Empty when clean. */
  notes: string;
}

// ============================================================================
// Stage × Mechanism matrix (Canon 23 §7.2)
// ============================================================================

type CellVerdict = readonly [ScenarioValidity, string | null];

const STAGE_MECHANISM_MATRIX: Record<CanonicalSessionType, Record<MechanismTag, CellVerdict>> = {
  participants: {
    categorization: ['valid', null],
    allocation: ['valid', null],
    threshold_sorting: ['valid', null],
    direct_ranking: ['warning', 'Ranking participants unusual; prefer categorization or allocation'],
    pairwise_comparison: ['warning', 'Overkill for participant selection'],
    weighted_scoring: ['not_valid', 'No criteria established yet'],
    sequential_multi_pass: ['not_valid', 'Too complex for participant selection'],
    tradeoff_lens: ['not_valid', 'Not applicable to participation decisions'],
  },
  item_pool: {
    categorization: ['valid', null],
    threshold_sorting: ['valid', null],
    allocation: ['valid', null],
    tradeoff_lens: ['valid', null],
    direct_ranking: ['warning', 'Ranking items before criteria defined; use with caution'],
    pairwise_comparison: ['warning', 'Overkill for pool selection without criteria'],
    weighted_scoring: ['not_valid', 'Criteria not yet established'],
    sequential_multi_pass: ['warning', 'Multi-pass pool formation possible but unusual'],
  },
  criteria: {
    categorization: ['valid', null],
    allocation: ['valid', null],
    threshold_sorting: ['valid', null],
    pairwise_comparison: ['valid', null],
    weighted_scoring: ['valid', null],
    sequential_multi_pass: ['valid', null],
    tradeoff_lens: ['valid', null],
    direct_ranking: ['warning', 'Can rank criteria but pairwise or allocation preferred'],
  },
  scales: {
    categorization: ['valid', null],
    threshold_sorting: ['valid', null],
    sequential_multi_pass: ['valid', null],
    tradeoff_lens: ['valid', null],
    direct_ranking: ['warning', 'Unusual for scale definition'],
    pairwise_comparison: ['not_valid', "Scale values don't compare against each other"],
    weighted_scoring: ['not_valid', 'Scales are not scored'],
    allocation: ['not_valid', 'Allocation not applicable to scale definition'],
  },
  values: {
    weighted_scoring: ['valid', null],
    pairwise_comparison: ['valid', null],
    categorization: ['valid', null],
    threshold_sorting: ['valid', null],
    sequential_multi_pass: ['valid', null],
    tradeoff_lens: ['valid', null],
    direct_ranking: ['warning', 'Direct ranking of values unusual; prefer weighted scoring or pairwise'],
    allocation: ['warning', 'Allocation for value assignment possible but atypical'],
  },
  items: {
    direct_ranking: ['required', 'Primary force ranking method'],
    pairwise_comparison: ['valid', null],
    weighted_scoring: ['valid', null],
    allocation: ['valid', null],
    sequential_multi_pass: ['valid', null],
    categorization: ['warning', 'Produces tiered output, not strict total order'],
    threshold_sorting: ['warning', 'Produces tiered output, not strict total order'],
    tradeoff_lens: ['warning', 'Narrative output; does not produce force ranking'],
  },
};

// Combination severity: not_valid > warning > required > valid
const COMBINE_ORDER: Record<ScenarioValidity, number> = {
  valid: 0,
  required: 1,
  warning: 2,
  not_valid: 3,
};

// ============================================================================
// Evaluation
// ============================================================================

/**
 * Evaluate a single 7-dimension scenario. Mirrors the Python oracle's
 * `evaluate_combination` exactly — rule order matters for note ordering.
 */
export function evaluateScenario(input: ScenarioInput): ScenarioVerdict {
  const { context, stage, runMode, mechanism, exchangeState, tournament, prerequisites } = input;
  const results: CellVerdict[] = [];

  // --- Hard invalids ---

  if (exchangeState !== 'not_applicable' && context !== 'cycle') {
    results.push(['not_valid', 'Exchange/Catchball only valid in Cycle context']);
  }

  if (mechanism === 'weighted_scoring' && prerequisites !== 'all_met') {
    results.push(['not_valid', 'Weighted scoring requires VALUES_COMPLETE (all prerequisites met)']);
  }

  if (stage === 'items' && prerequisites === 'none') {
    results.push(['not_valid', 'Force ranking requires at minimum partial prerequisites (pool must exist)']);
  }

  if (tournament === 'tournament_round' && (stage === 'participants' || stage === 'item_pool' || stage === 'scales')) {
    results.push(['not_valid', 'Tournament mode not applicable for this stage']);
  }

  // --- Stage × Mechanism matrix ---

  results.push(STAGE_MECHANISM_MATRIX[stage][mechanism]);

  // --- Additional warnings ---

  if (context === 'personal' && runMode === 'facilitator_live') {
    results.push(['warning', 'Facilitator-live mode in personal context is degenerate; single user enters own data']);
  }

  if (context === 'personal' && runMode === 'participant_live') {
    results.push(['warning', 'Participant-live in personal context; no group to coordinate']);
  }

  if (tournament === 'tournament_round' && (stage === 'criteria' || stage === 'values')) {
    results.push(['warning', 'Tournament mode unusual for configuration stages']);
  }

  if (stage === 'items' && runMode === 'open') {
    results.push(['warning', 'Open (async) mode for force ranking possible but unusual; real-time facilitation preferred']);
  }

  if (
    stage === 'items'
    && prerequisites === 'partial'
    && (mechanism === 'pairwise_comparison' || mechanism === 'direct_ranking' || mechanism === 'allocation')
  ) {
    results.push(['warning', 'Force ranking with partial prerequisites; pool and participation must be minimally satisfied']);
  }

  // --- Combine: any not_valid wins; else warning; else required; else valid ---

  let validity: ScenarioValidity = 'valid';
  const notes: string[] = [];
  for (const [level, note] of results) {
    if (note) notes.push(note);
    if (COMBINE_ORDER[level] > COMBINE_ORDER[validity]) validity = level;
  }

  return { validity, notes: notes.join('; ') };
}

/**
 * Convenience gate for the most common call site: validating a stage +
 * mechanism choice in the session config modal before full scenario context
 * is known. Defaults the remaining dimensions to their most permissive
 * values so only the stage × mechanism cell (plus the prerequisites rules)
 * contributes to the verdict.
 */
export function validateStageMechanismFit(
  stage: CanonicalSessionType,
  mechanism: MechanismTag,
  prerequisites: ScenarioPrerequisites = 'all_met'
): ScenarioVerdict {
  return evaluateScenario({
    context: 'group',
    stage,
    runMode: 'participant_live',
    mechanism,
    exchangeState: 'not_applicable',
    tournament: 'single_run',
    prerequisites,
  });
}

/** True when session creation must be hard-blocked for this combination. */
export function isBlocked(verdict: ScenarioVerdict): boolean {
  return verdict.validity === 'not_valid';
}
